import { Navigate, type RouteObject } from 'react-router-dom';

import { Layout } from './components/Layout';
import { RouteGuard } from './components/RouteGuard';
import { adminRoutes } from './features/admin/routes';
import { docsRoutes } from './features/api-docs/routes';
import { configRoutes } from './features/config/routes';
import { dashboardRoutes } from './features/dashboard/routes';
import { discoveryRoutes } from './features/document-types/discovery/routes';
import { sectorTemplateRoutes } from './features/document-types/templates/routes';
import { reviewerRoutes } from './features/reviewer/routes';
import { schemaRoutes } from './features/schema/routes';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * Route table for the entire app. Each authenticated route is wrapped in a
 * `<RouteGuard>` so that scope checks happen before the page renders.
 *
 * Phase 5 feature routes are contributed via dedicated `routes.tsx` exporters
 * under `src/features/<area>/` so each feature owns its surface area without
 * touching this file's structure.
 */
export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forbidden',
    element: <ForbiddenPage />,
  },
  // Public API documentation — no auth, its own shell. Sits alongside /login rather than inside the
  // authenticated Layout so an integrator can evaluate the API without an account.
  ...docsRoutes,
  {
    element: (
      <RouteGuard requireAuth>
        <Layout />
      </RouteGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      // Wave 5 — Dashboard + paginated documents listing (lazy-loaded).
      ...dashboardRoutes,
      // Wave 3 — Document Reviewer (PDF.js + bounding-box overlay).
      ...reviewerRoutes,
      // Phase 6 Wave 11.F — Discovery wizard + improvement signals. Mounted
      // BEFORE schemaRoutes so the `/document-types/discover` literal wins
      // over the `:slug` route in DocumentTypesListPage's siblings.
      ...discoveryRoutes,
      // Phase 8 Wave 8.E — Sector template catalog. Same precedence rule:
      // mounted before schemaRoutes so `/document-types/templates` wins.
      ...sectorTemplateRoutes,
      // Wave 2 — Schema Designer.
      ...schemaRoutes,
      // Wave 4 — Config (providers / webhooks / license).
      ...configRoutes,
    ],
  },
  // Platform admin panel — separate layout + auth context, must be before the wildcard.
  ...adminRoutes,
  { path: '*', element: <NotFoundPage /> },
];
